import { RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import "./SketchCanvas.css";

const COLORS = ["#3b215c", "#8652dc", "#ef8fbd", "#2f9e8a", "#f0b429", "#ffffff"];
const SIZES = [4, 9, 16];
const DRAFT_INTERVAL_MS = 200;

type Props = {
  disabled?: boolean;
  variant?: "game" | "avatar";
  onExport: (dataUrl: string) => void;
  onDraft?: (dataUrl: string) => void;
  initialImageUrl?: string;
  deadlineAt?: number | null;
};

export function SketchCanvas({ disabled, variant = "game", onExport, onDraft, initialImageUrl, deadlineAt }: Props) {
  const avatar = variant === "avatar";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<{ color: string; size: number; points: { x: number; y: number }[] }[]>([]);
  const baseImage = useRef<HTMLImageElement | null>(null);
  const drawing = useRef(false);
  const changed = useRef(false);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastDraftAt = useRef(-Infinity);
  const draftCallback = useRef(onDraft);
  draftCallback.current = onDraft;
  const [restoring, setRestoring] = useState(Boolean(initialImageUrl));
  const [expired, setExpired] = useState(deadlineAt != null && Date.now() >= deadlineAt);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(9);
  const [tick, setTick] = useState(0);
  const blocked = disabled || restoring || expired;
  const hasDrawing = strokes.current.length > 0 || Boolean(baseImage.current);

  function point(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const box = canvas.getBoundingClientRect();
    return { x: (event.clientX - box.left) * (canvas.width / box.width), y: (event.clientY - box.top) * (canvas.height / box.height) };
  }

  function paint() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fffdf8";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (baseImage.current) ctx.drawImage(baseImage.current, 0, 0, canvas.width, canvas.height);
    for (const stroke of strokes.current) {
      if (!stroke.points.length) continue;
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.size;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (const next of stroke.points.slice(1)) ctx.lineTo(next.x, next.y);
      if (stroke.points.length === 1) ctx.lineTo(stroke.points[0].x + 0.1, stroke.points[0].y);
      ctx.stroke();
    }
  }

  function imageData() {
    const source = canvasRef.current;
    if (!source) return "";
    paint(); // Include points received before React has painted the next frame.
    const out = document.createElement("canvas");
    out.width = avatar ? 512 : 640;
    out.height = avatar ? 512 : 360;
    const ctx = out.getContext("2d");
    if (!ctx) return "";
    ctx.fillStyle = "#fffdf8";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(source, 0, 0, out.width, out.height);
    const png = out.toDataURL("image/png");
    return !avatar && png.length > 400_000 ? out.toDataURL("image/webp", 0.85) : png;
  }

  function flushDraft() {
    clearTimeout(draftTimer.current);
    draftTimer.current = undefined;
    if (!changed.current || !draftCallback.current || restoring) return;
    lastDraftAt.current = Date.now();
    draftCallback.current(strokes.current.length || baseImage.current ? imageData() : "");
  }
  const flushRef = useRef(flushDraft);
  flushRef.current = flushDraft;

  function change(immediate = false) {
    changed.current = true;
    setTick((value) => value + 1);
    if (!draftCallback.current) return;
    if (immediate || Date.now() - lastDraftAt.current >= DRAFT_INTERVAL_MS) flushDraft();
    else if (draftTimer.current == null) draftTimer.current = setTimeout(() => flushRef.current(), DRAFT_INTERVAL_MS - (Date.now() - lastDraftAt.current));
  }

  function stopStroke() {
    if (!drawing.current) return;
    drawing.current = false;
    flushDraft();
  }

  useEffect(() => { paint(); }, [tick]);
  useEffect(() => {
    if (!initialImageUrl) return;
    let active = true;
    const image = new Image();
    image.onload = () => { if (active) { baseImage.current = image; setRestoring(false); setTick((value) => value + 1); } };
    image.onerror = () => { if (active) setRestoring(false); };
    image.src = initialImageUrl;
    return () => { active = false; };
  }, [initialImageUrl]);
  useEffect(() => {
    setExpired(deadlineAt != null && Date.now() >= deadlineAt);
    if (deadlineAt == null) return;
    const timer = setTimeout(() => {
      drawing.current = false;
      flushRef.current(); // A pointer can still be held down at the deadline.
      setExpired(true);
    }, Math.max(0, deadlineAt - Date.now()));
    return () => clearTimeout(timer);
  }, [deadlineAt]);
  useEffect(() => {
    const leaving = () => flushRef.current();
    document.addEventListener("visibilitychange", leaving);
    window.addEventListener("pagehide", leaving);
    return () => { clearTimeout(draftTimer.current); document.removeEventListener("visibilitychange", leaving); window.removeEventListener("pagehide", leaving); };
  }, []);

  function exportPng() {
    if (blocked || !hasDrawing) return;
    const dataUrl = imageData();
    if (dataUrl) onExport(dataUrl);
  }

  return <div className={`sketch-canvas-wrap${avatar ? " sketch-canvas-avatar" : ""}`}>
    <canvas
      ref={canvasRef}
      width={avatar ? 768 : 960}
      height={avatar ? 768 : 540}
      aria-label={avatar ? "Draw your avatar" : "Drawing canvas"}
      aria-disabled={blocked || undefined}
      onPointerDown={(event) => {
        if (blocked || (deadlineAt != null && Date.now() >= deadlineAt) || event.button !== 0 || drawing.current) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        drawing.current = true;
        strokes.current = [...strokes.current, { color, size, points: [point(event)] }];
        change();
      }}
      onPointerMove={(event) => {
        if (!drawing.current || blocked || (deadlineAt != null && Date.now() >= deadlineAt)) return;
        const current = strokes.current[strokes.current.length - 1];
        if (!current) return;
        current.points.push(point(event));
        change();
      }}
      onPointerUp={stopStroke}
      onPointerCancel={stopStroke}
      onLostPointerCapture={stopStroke}
    />
    <div className="sketch-tools">
      <div>{COLORS.map((value) => <button type="button" key={value} className={color === value ? "selected" : ""} style={{ background: value }} aria-label={`Colour ${value}`} aria-pressed={color === value} disabled={blocked} onClick={() => setColor(value)} />)}</div>
      {avatar && <label className="sketch-custom-colour">More colours<input type="color" value={color} aria-label="Choose a drawing colour" disabled={blocked} onChange={(event) => setColor(event.target.value)} /></label>}
      <div>{SIZES.map((value) => <button type="button" key={value} className={size === value ? "selected" : ""} aria-label={`${value}px brush`} aria-pressed={size === value} disabled={blocked} onClick={() => setSize(value)}>{value}px</button>)}</div>
      <button type="button" disabled={blocked || !strokes.current.length} onClick={() => { drawing.current = false; strokes.current = strokes.current.slice(0, -1); change(true); }}><RotateCcw size={15} /> Undo</button>
      <button type="button" disabled={blocked} onClick={() => { drawing.current = false; strokes.current = []; baseImage.current = null; change(true); }}><Trash2 size={15} /> Clear</button>
      <button type="button" className="sketch-submit" disabled={blocked || !hasDrawing} onClick={exportPng}>{avatar ? "Use this avatar" : "Send drawing"}</button>
    </div>
  </div>;
}
